import React from 'react';
import Button, { ButtonProps } from '@amzn/awsui-components-react/polaris/button';
import Link, { LinkProps } from '@amzn/awsui-components-react/polaris/link';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';

type Callback = (event: CustomEvent) => void;
//this will infer the right link from the href on the source link
//but if that is not set for whatever reason you can override it.
export function useSpaWrapper() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return (opts: { url?: string; onFollow?: Callback; state?: unknown }) =>
    wrapSpaOnFollow(Object.assign({}, { navigate, searchParams }, opts));
}

function savePersistantParams(
  oldParams: URLSearchParams,
  newParams: URLSearchParams,
  persistantParams: string[]
) {
  persistantParams.forEach((paramKey) => {
    const value = oldParams.get(paramKey);
    if (!newParams.get(paramKey) && value) {
      newParams.set(paramKey, value);
    }
  });
}
// Edit url or href to ensure persistant url params are maintained
// on any internal links.
// Exported so callers that navigate outside of a Cloudscape onFollow event
// (e.g. a confirmation modal's onConfirm) can preserve the same params that
// wrapSpaOnFollow does, instead of calling navigate() with a bare href.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function urlWithPersistantParams(searchParams: URLSearchParams, url: any) {
  if (!url || !url.startsWith('/')) return url;

  // Using a fake base url here prevents issues with the relative url not
  // matching a url schema, and allows us to remove it later without
  // worrying about removing redirects
  const urlObj = new URL(url, 'https://redvelvet-fakeurl.com');
  const params = urlObj.searchParams;
  savePersistantParams(searchParams, params, ['features', 'timezone']);
  return urlObj.toString().replace('https://redvelvet-fakeurl.com', '');
}

export function wrapSpaOnFollow(opts: {
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  onFollow?: Callback;
  url?: string;
  state?: unknown;
}): Callback {
  return (event: CustomEvent) => {
    if (opts.onFollow) {
      opts.onFollow(event);
    }
    event.preventDefault();
    const url = opts.url ?? event.detail.href;
    opts.navigate(urlWithPersistantParams(opts.searchParams, url), { state: opts.state });
  };
}
//https://github.com/cloudscape-design/components/discussions/1699
/**
 * In order to support the polaris link AND correctly integrate with
 * router navigation (so as to not reload the app every time you click a link),
 * you need to specify href AND onFollow.
 * @param props
 * @returns
 */
export function SpaLink(props: LinkProps & { state?: unknown }) {
  const spaWrapper = useSpaWrapper();
  const [searchParams] = useSearchParams();
  const { state, ...linkProps } = props;

  const propsWithOnFollow = Object.assign({}, linkProps, {
    onFollow: spaWrapper({ onFollow: props.onFollow, url: props.href, state }),
  });
  return <Link {...propsWithOnFollow} href={urlWithPersistantParams(searchParams, props.href)} />;
}

export function SpaButton(props: ButtonProps) {
  const spaWrapper = useSpaWrapper();
  const [searchParams] = useSearchParams();

  const propsWithOnFollow = Object.assign({}, props, {
    onFollow: spaWrapper({ onFollow: props.onFollow, url: props.href }),
  });
  return <Button {...propsWithOnFollow} href={urlWithPersistantParams(searchParams, props.href)} />;
}
